import React, { useCallback, useEffect, useState } from 'react'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import { MapPin, Loader2, AlertCircle, Trash2, Pencil, Plus, X, CheckCircle2, Archive, Languages } from 'lucide-react'
import {
  listFacilitiesAdmin,
  getFacilityStats,
  createFacility,
  updateFacility,
  changeFacilityStatus,
  deleteFacility,
  FACILITY_TYPE_LABELS,
  FACILITY_STATUS_LABELS,
  type Facility,
  type FacilityRequest,
  type FacilityType,
  type FacilityStatus,
} from '@/services/facilityService'
import { TranslationEditorModal, type TranslationFieldDef } from '@/components/admin/TranslationEditorModal'
import { type TranslationRow } from '@/services/translationService'

const STATUS_FILTERS: (FacilityStatus | 'ALL')[] = ['ALL', 'PENDING', 'VERIFIED', 'ARCHIVED']
const TYPES = Object.keys(FACILITY_TYPE_LABELS) as FacilityType[]
const STATUS_LIST: FacilityStatus[] = ['PENDING', 'VERIFIED', 'ARCHIVED']

const statusTone: Record<FacilityStatus, string> = {
  PENDING: 'bg-amber-100 text-amber-800',
  VERIFIED: 'bg-green-100 text-green-700',
  ARCHIVED: 'bg-slate-100 text-slate-600',
}

const EMPTY_FORM: FacilityRequest = {
  name: '', facilityType: 'DMC', address: '', district: '', state: '', pincode: '',
  phone: '', email: '', latitude: null, longitude: null, servicesOffered: '',
  operatingHours: '', source: '', status: 'PENDING', isVerified: false,
}

const FacilitiesManagement = React.memo(() => {
  const [items, setItems] = useState<Facility[]>([])
  const [stats, setStats] = useState<{ TOTAL: number; GEOCODED: number; PENDING: number; VERIFIED: number; ARCHIVED: number }>({ TOTAL: 0, GEOCODED: 0, PENDING: 0, VERIFIED: 0, ARCHIVED: 0 })
  const [statusFilter, setStatusFilter] = useState<FacilityStatus | 'ALL'>('ALL')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actingId, setActingId] = useState<number | null>(null)
  const [toast, setToast] = useState<{ tone: 'success' | 'error'; msg: string } | null>(null)
  const [editing, setEditing] = useState<Facility | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<FacilityRequest>(EMPTY_FORM)
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [translateId, setTranslateId] = useState<number | null>(null)
  const size = 20

  const TRANSLATE_FIELDS: TranslationFieldDef[] = [
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'address', label: 'Address', type: 'textarea', rows: 2 },
    { key: 'servicesOffered', label: 'Services offered', type: 'textarea', rows: 2 },
    { key: 'operatingHours', label: 'Operating hours', type: 'text' },
    { key: 'source', label: 'Source', type: 'text' },
  ]

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const [data, s] = await Promise.all([
        listFacilitiesAdmin({ status: statusFilter === 'ALL' ? undefined : statusFilter, q: q || undefined, page, size }),
        getFacilityStats(),
      ])
      setItems(data.content); setTotalPages(data.totalPages); setStats(s)
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to load facilities.') }
    finally { setLoading(false) }
  }, [statusFilter, q, page])

  useEffect(() => { load() }, [load])
  useEffect(() => { setPage(0) }, [statusFilter, q])

  const flash = (tone: 'success' | 'error', msg: string) => { setToast({ tone, msg }); setTimeout(() => setToast(null), 3000) }

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setFormError(null); setShowForm(true) }
  const openEdit = (f: Facility) => {
    setEditing(f)
    setForm({
      name: f.name, facilityType: f.facilityType, address: f.address ?? '', district: f.district ?? '',
      state: f.state ?? '', pincode: f.pincode ?? '', phone: f.phone ?? '', email: f.email ?? '',
      latitude: f.latitude ?? null, longitude: f.longitude ?? null, servicesOffered: f.servicesOffered ?? '',
      operatingHours: f.operatingHours ?? '', source: f.source ?? '', status: f.status, isVerified: f.isVerified,
    })
    setFormError(null); setShowForm(true)
  }
  const closeForm = () => { setShowForm(false); setEditing(null); setForm(EMPTY_FORM); setFormError(null) }

  const save = async (ev: React.FormEvent) => {
    ev.preventDefault()
    if (!form.name.trim()) { setFormError('Name is required.'); return }
    if (!form.facilityType) { setFormError('Facility type is required.'); return }
    setSaving(true); setFormError(null)
    try {
      const payload: FacilityRequest = {
        ...form,
        name: form.name.trim(),
        latitude: form.latitude === null ? undefined : form.latitude,
        longitude: form.longitude === null ? undefined : form.longitude,
      }
      if (editing) { await updateFacility(editing.id, payload); flash('success', `Updated “${form.name}”`) }
      else { await createFacility(payload); flash('success', `Added “${form.name}”`) }
      closeForm(); await load()
    } catch (e: any) { setFormError(e?.response?.data?.message || 'Failed to save.') }
    finally { setSaving(false) }
  }

  const changeStatus = async (f: Facility, status: FacilityStatus) => {
    setActingId(f.id)
    try { await changeFacilityStatus(f.id, status); flash('success', `“${f.name}” → ${FACILITY_STATUS_LABELS[status]}`); await load() }
    catch (e: any) { flash('error', e?.response?.data?.message || 'Failed.') }
    finally { setActingId(null) }
  }

  const remove = async (f: Facility) => {
    if (!window.confirm(`Delete “${f.name}”?`)) return
    setActingId(f.id)
    try { await deleteFacility(f.id); flash('success', 'Facility deleted'); await load() }
    catch (e: any) { flash('error', e?.response?.data?.message || 'Failed.') }
    finally { setActingId(null) }
  }

  return (
    <GovLayout showSidebar={false}>
      <div className="space-y-6">
        <GovPageTitle title="Facility Directory" subtitle="Add and verify TB testing/treatment facilities from a verified official source" icon={MapPin} />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <GovCard className="text-center"><p className="text-2xl font-bold text-gray-900">{stats.TOTAL}</p><p className="text-sm text-gray-500">Total</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-amber-600">{stats.PENDING}</p><p className="text-sm text-gray-500">Pending</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-green-600">{stats.VERIFIED}</p><p className="text-sm text-gray-500">Verified</p></GovCard>
          <GovCard className="text-center"><p className="text-2xl font-bold text-teal-600">{stats.GEOCODED}</p><p className="text-sm text-gray-500">Geocoded</p></GovCard>
        </div>

        <GovCard>
          <div className="flex flex-wrap items-center gap-2">
            {STATUS_FILTERS.map((f) => (
              <button key={f} onClick={() => setStatusFilter(f)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium ${statusFilter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {f === 'ALL' ? 'All' : FACILITY_STATUS_LABELS[f]}
              </button>
            ))}
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name/district/state…" className="text-sm border border-gray-200 rounded-md px-3 py-1.5 ml-auto" />
            <GovButton variant="primary" size="sm" onClick={openCreate} icon={<Plus className="w-4 h-4" />}>Add facility</GovButton>
          </div>
        </GovCard>

        {toast && (
          <div role="status" className={`flex items-center gap-2 p-3 rounded-lg text-sm ${toast.tone === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {toast.tone === 'success' ? '✓' : <AlertCircle className="w-4 h-4" />} {toast.msg}
          </div>
        )}

        <GovCard>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-bold text-gray-900">Facilities</h2>
            <button onClick={load} disabled={loading} className="text-xs text-blue-600 hover:underline disabled:opacity-50">{loading ? 'Loading…' : 'Refresh'}</button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
          ) : error ? (
            <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg text-sm"><AlertCircle className="w-4 h-4" /> {error}</div>
          ) : items.length === 0 ? (
            <GovEmptyState icon="🏥" title="No facilities" description="Add a facility from a verified official source, or adjust the filter." />
          ) : (
            <div className="divide-y divide-gray-100">
              {items.map((f) => (
                <div key={f.id} className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${statusTone[f.status]}`}>{FACILITY_STATUS_LABELS[f.status]}</span>
                        <span className="text-xs text-gray-500">{FACILITY_TYPE_LABELS[f.facilityType]}</span>
                        {f.latitude != null && <span className="text-xs text-teal-600">· geocoded</span>}
                      </div>
                      <h4 className="font-medium text-gray-900">{f.name}</h4>
                      <p className="text-xs text-gray-400 mt-1">
                        {[f.district, f.state, f.pincode].filter(Boolean).join(', ')}
                        {f.phone && ` · ${f.phone}`}
                      </p>
                      {f.address && <p className="text-sm text-gray-600 mt-1 line-clamp-1">{f.address}</p>}
                      {f.source && <p className="text-[11px] text-gray-400 mt-1">Source: {f.source}</p>}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                      {actingId === f.id && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                      {f.status !== 'VERIFIED' && <GovButton variant="ghost" size="sm" onClick={() => changeStatus(f, 'VERIFIED')} icon={<CheckCircle2 className="w-3.5 h-3.5" />}>Verify</GovButton>}
                      {f.status === 'VERIFIED' && <GovButton variant="ghost" size="sm" onClick={() => changeStatus(f, 'ARCHIVED')} icon={<Archive className="w-3.5 h-3.5" />}>Archive</GovButton>}
                      <button onClick={() => openEdit(f)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-md" aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => setTranslateId(f.id)} className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md" aria-label="Translate"><Languages className="w-4 h-4" /></button>
                      <button onClick={() => remove(f)} disabled={actingId === f.id} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>Previous</GovButton>
              <span className="text-xs text-gray-500">Page {page + 1} of {totalPages}</span>
              <GovButton variant="secondary" size="sm" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next</GovButton>
            </div>
          )}
        </GovCard>

        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" onClick={closeForm}>
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{editing ? 'Edit facility' : 'Add facility'}</h3>
                  <p className="text-sm text-gray-500 mt-1">Add only verified facilities from an official source. Do not fabricate addresses, phone numbers or coordinates.</p>
                </div>
                <button onClick={closeForm} className="text-gray-400 hover:text-gray-700" aria-label="Close"><X className="w-5 h-5" /></button>
              </div>

              {formError && <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 text-red-800 rounded-lg text-sm" role="alert"><AlertCircle className="w-4 h-4" /> {formError}</div>}

              <form onSubmit={save} noValidate className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="f-name">Name <span className="text-red-500">*</span></label>
                    <input id="f-name" type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="f-type">Type <span className="text-red-500">*</span></label>
                    <select id="f-type" value={form.facilityType} onChange={(e) => setForm({ ...form, facilityType: e.target.value as FacilityType })} className="select">
                      {TYPES.map((t) => <option key={t} value={t}>{FACILITY_TYPE_LABELS[t]}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="f-addr">Address</label>
                  <input id="f-addr" type="text" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="input" />
                </div>
                <div className="grid sm:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="f-state">State</label>
                    <input id="f-state" type="text" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="f-dist">District</label>
                    <input id="f-dist" type="text" value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} className="input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="f-pin">Pincode</label>
                    <input id="f-pin" type="text" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} className="input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="f-status">Status</label>
                    <select id="f-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as FacilityStatus })} className="select">
                      {STATUS_LIST.map((s) => <option key={s} value={s}>{FACILITY_STATUS_LABELS[s]}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="f-phone">Phone</label>
                    <input id="f-phone" type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="f-src">Source</label>
                    <input id="f-src" type="text" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="input" placeholder="e.g. State TB directory 2024" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="f-svc">Services offered</label>
                  <input id="f-svc" type="text" value={form.servicesOffered} onChange={(e) => setForm({ ...form, servicesOffered: e.target.value })} className="input" placeholder="e.g. sputum test, TB medicine, rapid molecular test (CBNAAT)" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="f-lat">Latitude (optional — for “near me”)</label>
                    <input id="f-lat" type="number" step="any" value={form.latitude ?? ''} onChange={(e) => setForm({ ...form, latitude: e.target.value === '' ? null : Number(e.target.value) })} className="input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="f-lng">Longitude (optional)</label>
                    <input id="f-lng" type="number" step="any" value={form.longitude ?? ''} onChange={(e) => setForm({ ...form, longitude: e.target.value === '' ? null : Number(e.target.value) })} className="input" />
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <GovButton type="submit" variant="primary" disabled={saving} icon={saving ? <Loader2 className="w-4 h-4 animate-spin" /> : undefined}>{saving ? 'Saving…' : 'Save facility'}</GovButton>
                  <GovButton variant="ghost" onClick={closeForm}>Cancel</GovButton>
                </div>
              </form>
            </div>
          </div>
        )}

        <TranslationEditorModal
          open={translateId !== null}
          basePath="/facilities"
          id={translateId}
          itemLabel="facility"
          fields={TRANSLATE_FIELDS}
          canonical={translateId !== null ? (items.find((f) => f.id === translateId) as unknown as Partial<TranslationRow> | undefined) : undefined}
          onClose={() => setTranslateId(null)}
        />
      </div>
    </GovLayout>
  )
})

FacilitiesManagement.displayName = 'FacilitiesManagement'
export default FacilitiesManagement